/**
 * Why the AI did not answer, told apart at the level the person has to act on.
 *
 * Pure: no React, no clock, no request.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  TWO 429s THAT MEAN OPPOSITE THINGS
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `POST /ai/chat` can refuse with two different `429`s, and API_CONTRACT §8.9 makes
 * both first-class:
 *
 *   `WORKFORCE_RATE_LIMITED`   the process's two concurrent slots are taken.
 *                              `details` carry `limit`, `in_flight` and
 *                              `retry_after_seconds`. **A slot frees. Wait.**
 *   `WORKFORCE_QUOTA_EXCEEDED` the company is over its monthly soft limit.
 *                              `details` carry `period_month`, `cost_usd` and
 *                              `limit_usd`. **Nothing frees.** The month has to roll
 *                              over or an operator has to raise `WP_AI_SOFT_LIMIT`,
 *                              and nobody raises a limit they were never told about.
 *
 * The HTTP status is identical; only `code` separates them, which is API_CONTRACT §5's
 * rule ("branch on `code`, never on `message`") mattering for something other than
 * tidiness. Rendering both as one retryable failure puts a *Thử lại* button under the
 * second, and every press of it is a request the gateway refuses before it is sent.
 *
 * That is the same distinction `scoring/freshness.ts` keeps between `scheduled` and
 * `requires_request`, and this module keeps it the same way — two variants, asserted
 * apart rather than asserted to both exist.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE BUDGET VARIANT CARRIES NO RETRY FIELD, SO NO COMPONENT CAN OFFER ONE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `retryAfterSeconds` exists on `busy` and **nowhere else**. A budget refusal has no
 * seconds to wait, and a shape that offered the field would let a renderer print
 * "thử lại sau 0 giây" — the D-22 shape one surface over, where an absence becomes a
 * zero and the zero becomes advice.
 *
 * This is `incidents/sla.ts`'s technique (the breached variant carries no remaining
 * time) and `reports/download.ts`'s (no state carries a URL): remove the field rather
 * than validate it.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE SERVER'S OWN SENTENCE IS CARRIED, NOT REPLACED
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Task 45 shipped a commit fixing 27 swallowed refusals. Every variant below carries
 * `serverSentence`, which is `error.message` when API_CONTRACT §5.1 marks the code
 * safe to show and `null` when it does not — the same decision `states/ApiErrorNote.tsx`
 * makes, taken once here so the AI screen's own panels cannot forget it.
 */

import { isWorkforceApiError, permissionDeniedCode, type WorkforceApiError } from '../api/errors';

function detailNumber(error: WorkforceApiError, key: string): number | null {
  const value = error.details[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * `cost_usd` and `limit_usd` are money and arrive as **strings** — `numeric` columns
 * rendered by PostgreSQL, kept as text so no digit is lost to a double. They are
 * printed verbatim and never parsed: a client that turned `"12.30"` into `12.3` would
 * be re-rendering a company's spend with its own arithmetic.
 */
function detailMoney(error: WorkforceApiError, key: string): string | null {
  const value = error.details[key];
  if (typeof value === 'string' && value.trim() !== '') return value;
  // A server that publishes it as a number is still readable; `String` here is a
  // rendering of a value nobody computed with, not a conversion.
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function detailText(error: WorkforceApiError, key: string): string | null {
  const value = error.details[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** `error.message` if §5.1 says it may be shown, otherwise `null`. */
function sentenceOf(error: WorkforceApiError): string | null {
  return error.userSafe ? error.message : null;
}

export type AiRefusal =
  /**
   * The company is over its monthly AI budget. **No retry field anywhere in this
   * variant** — see the header.
   */
  | {
      readonly kind: 'budget';
      readonly periodMonth: string | null;
      readonly costUsd: string | null;
      readonly limitUsd: string | null;
      readonly serverSentence: string | null;
      readonly code: string;
      readonly correlationId: string | null;
    }
  /** Every synchronous slot is taken. One frees; the server says roughly when. */
  | {
      readonly kind: 'busy';
      readonly retryAfterSeconds: number | null;
      readonly inFlight: number | null;
      readonly limit: number | null;
      readonly serverSentence: string | null;
      readonly code: string;
      readonly correlationId: string | null;
    }
  /**
   * The gateway is unreachable, mis-keyed, or the breaker is open.
   *
   * On `POST /ai/chat` this is **not** normally an error at all — the endpoint
   * answers `200` with WP-M2's arithmetic fallback — so reaching this variant means
   * the refusal came from somewhere the fallback does not cover, and the screen says
   * so instead of pretending a fallback happened.
   */
  | {
      readonly kind: 'unavailable';
      readonly serverSentence: string | null;
      readonly code: string;
      readonly correlationId: string | null;
    }
  /** One of the four chain refusals. Rendered by the shared `PermissionDenied`. */
  | { readonly kind: 'denied'; readonly code: string; readonly correlationId: string | null }
  /** Anything else, including transport. The shared `ApiErrorNote` renders it. */
  | { readonly kind: 'other'; readonly code: string };

/**
 * The error, classified.
 *
 * `WORKFORCE_UPSTREAM_RATE_LIMITED` is deliberately **not** folded into `busy`: it is
 * the *gateway* rate-limiting this tenant, which is a fact about LiteLLM rather than
 * about this process's two slots, and the remedy is not "wait for a colleague's
 * question to finish". It falls through to `other`, where the server's own sentence
 * is what the reader gets.
 */
export function readAiRefusal(error: unknown): AiRefusal {
  if (!isWorkforceApiError(error)) return { kind: 'other', code: 'CLIENT_TRANSPORT_FAILED' };
  const denied = permissionDeniedCode(error);
  if (denied !== null) {
    return { kind: 'denied', code: denied, correlationId: error.correlationId };
  }
  switch (error.code) {
    case 'WORKFORCE_QUOTA_EXCEEDED':
      return {
        kind: 'budget',
        periodMonth: detailText(error, 'period_month'),
        costUsd: detailMoney(error, 'cost_usd'),
        limitUsd: detailMoney(error, 'limit_usd'),
        serverSentence: sentenceOf(error),
        code: error.code,
        correlationId: error.correlationId,
      };
    case 'WORKFORCE_RATE_LIMITED':
      return {
        kind: 'busy',
        retryAfterSeconds: error.retryAfterSeconds,
        inFlight: detailNumber(error, 'in_flight'),
        limit: detailNumber(error, 'limit'),
        serverSentence: sentenceOf(error),
        code: error.code,
        correlationId: error.correlationId,
      };
    case 'WORKFORCE_AI_UNAVAILABLE':
    case 'WORKFORCE_CIRCUIT_OPEN':
      return {
        kind: 'unavailable',
        serverSentence: sentenceOf(error),
        code: error.code,
        correlationId: error.correlationId,
      };
    default:
      return { kind: 'other', code: error.code };
  }
}

/**
 * May the screen offer a retry for this refusal?
 *
 * `busy` yes — a slot frees on its own. `budget` **no**, and that is the whole point:
 * `api/errors.ts`'s `isRetryable` says yes to every `429`, which is right for the
 * generic note and wrong here, and this is the one place that knows the difference.
 */
export function refusalIsRetryable(refusal: AiRefusal): boolean {
  return refusal.kind === 'busy';
}
